import type { EventTemplateBindingSlot, Template } from '@/api/templates';
import { Button, ChannelName } from '@/components/ui';
import { getTechnicalTypeLabel } from '@/features/templates/templateTaxonomy';
import { slotStatusLabel } from './bindingStatus';

type Props = {
  slot: EventTemplateBindingSlot;
  options: Template[];
  saving: boolean;
  onSet: (templateId: string) => void;
  onClear: () => void;
  onOpen: (templateId: string) => void;
};

export function ChannelBindingRow({ slot, options, saving, onSet, onClear, onOpen }: Props) {
  const hasTemplate = Boolean(slot.template?.id);
  const status = slotStatusLabel(slot, 'override');
  const statusTone = hasTemplate
    ? slot.template?.is_active
      ? 'text-shell-success'
      : 'text-shell-warning'
    : 'text-shell-muted';

  return (
    <div className="grid gap-2 rounded-xl border border-shell-border bg-shell-surface px-3 py-2.5 sm:grid-cols-[minmax(8rem,11rem)_minmax(0,1fr)_auto] sm:items-center">
      <div className="min-w-0">
        <ChannelName
          channelId={slot.channel}
          label={slot.label}
          size={18}
          className="text-sm font-semibold text-shell-text"
        />
        <div className={`mt-0.5 truncate text-xs ${statusTone}`} title={status}>
          {status}
        </div>
      </div>

      <select
        className="w-full rounded-lg border border-shell-border bg-shell-elevated px-3 py-2 text-sm text-shell-text outline-none focus:border-shell-primary focus:ring-2 focus:ring-shell-primary/20"
        value={slot.template?.id || ''}
        disabled={saving}
        onChange={(event) => {
          const value = event.target.value;
          if (!value) return;
          onSet(value);
        }}
      >
        <option value="">Выберите override</option>
        {options.map((template) => (
          <option key={template.id} value={template.id}>
            {template.name} [{getTechnicalTypeLabel(template)}]
            {template.is_active ? '' : ' (неактивен)'}
          </option>
        ))}
      </select>

      <div className="flex flex-wrap items-center gap-1.5 sm:justify-end">
        {hasTemplate ? (
          <Button
            size="sm"
            variant="ghost"
            className="h-8 px-2.5 text-xs font-semibold"
            onClick={() => onOpen(slot.template!.id)}
          >
            Открыть
          </Button>
        ) : null}
        <Button
          size="sm"
          variant="secondary"
          className="h-8 px-2.5 text-xs font-semibold"
          disabled={saving || !slot.has_binding}
          onClick={onClear}
        >
          Очистить
        </Button>
      </div>
    </div>
  );
}
